/**
 * The words the reasoning line derives from a chain of thought and a clock.
 * Pure, so they are tested rather than watched stream past.
 */

/**
 * The end of a chain on one line: roughly the last `max` characters, cut at a
 * word, whitespace collapsed, led by "…" when anything was cut. The end,
 * because while a model thinks the newest thought is the one worth seeing.
 */
export function reasoningTail(text: string, max = 120): string {
  // Only the end is read; a chain can run to tens of thousands of characters
  // and this runs on every piece that arrives.
  const window = text.length > max * 4 ? text.slice(-max * 4) : text;
  const flat = window.replace(/\s+/g, ' ').trim();
  if (window === text && flat.length <= max) return flat;

  let tail = flat.slice(-max);
  // Started inside a word: drop the fragment, unless the whole tail is one
  // enormous word (a URL, a hash) and nothing would be left.
  if (flat[flat.length - max - 1] !== ' ') {
    const space = tail.indexOf(' ');
    if (space >= 0 && space < max / 2) tail = tail.slice(space + 1);
  }
  return `…${tail}`;
}

/**
 * The start of a sentence still being written, on one line: roughly the first
 * `max` characters, cut at a word, whitespace collapsed, and always followed
 * by "…", because more of it is coming.
 */
export function reasoningHead(text: string, max = 120): string {
  const flat = (text.length > max * 4 ? text.slice(0, max * 4) : text).replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return `${flat}…`;

  let head = flat.slice(0, max);
  // Ends inside a word: drop the fragment, on the same terms as the tail.
  if (flat[max] !== ' ') {
    const space = head.lastIndexOf(' ');
    if (space > max / 2) head = head.slice(0, space);
  }
  return `${head.trimEnd()}…`;
}

/**
 * How many characters of the tail fit a thinking line `width` px wide: less
 * the ~130px of sparkle, "Thinking" and caret, at 13px Inter's ~6.2px a
 * character rounded up, so the cut is at a word here rather than mid-word
 * where CSS clips. Never more than `max`.
 */
export function tailBudget(width: number, max = 120): number {
  return Math.max(16, Math.min(max, Math.floor((width - 130) / 6.6)));
}

// ── pacing ──────────────────────────────────────────────────────────────────

/** The least time a line stays up before a newer one takes its place. */
export const HOLD_MS = 1500;
/** How long a sentence runs without an end before its start is shown. */
export const OPEN_AFTER_MS = 3000;

/**
 * What the thinking line shows. A model thinks faster than anyone reads, so
 * the line is the newest *finished* sentence or line of the chain, held for
 * at least `HOLD_MS`; a burst of short ones skips to the newest when the hold
 * is up. A sentence that runs past `OPEN_AFTER_MS` with no end shows its
 * start instead, and before anything has finished the line is empty, which
 * shows "Thinking" alone.
 */
export interface Pace {
  /** Flattened and uncut: the component cuts it to the line's width, from
   *  its end, or from its start when `open`. Empty for "Thinking" alone. */
  line: string;
  /** Tells one line from the next, as it grows: where it starts in the
   *  chain, and whether it is finished. */
  id: string;
  /** `line` is the start of a sentence still being written. */
  open: boolean;
  /** When `line` went up. */
  shownAt: number;
  /** Where the sentence being written starts in the chain, and since when. */
  from: number;
  fromAt: number;
  /** When asking again with the same chain would change the line, or null
   *  when only more of the chain can. */
  wake: number | null;
}

const CLOSERS = `"')]’”`;
const flatten = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * A sentence or line ends just before `text[i]`: a newline, or whitespace
 * after `.`, `!` or `?` and any closing quote or bracket. A full stop after
 * a digit is a list number more often than an end, so it is not one.
 */
function endsAt(text: string, i: number): boolean {
  const c = text[i]!;
  if (c === '\n') return true;
  if (!/\s/.test(c)) return false;
  let j = i - 1;
  while (j >= 0 && CLOSERS.includes(text[j]!)) j--;
  const t = text[j];
  if (t === '!' || t === '?') return true;
  return t === '.' && !/\d/.test(text[j - 1] ?? '');
}

/** Where the sentence being written starts, and the finished one before it
 *  (blank lines skipped). Reads backwards, so only those two are scanned. */
function lastSentences(text: string): { from: number; last: { line: string; start: number } | null } {
  let end = text.length - 1;
  while (end >= 0 && !endsAt(text, end)) end--;
  const from = end + 1;

  while (end >= 0) {
    let start = end - 1;
    while (start >= 0 && !endsAt(text, start)) start--;
    const line = flatten(text.slice(start + 1, end));
    if (line) return { from, last: { line, start: start + 1 } };
    end = start;
  }
  return { from, last: null };
}

/** The line to show for `text` at `now` (ms), given what was showing. */
export function pace(prev: Pace | null, text: string, now: number): Pace {
  const { from, last } = lastSentences(text);
  const fromAt = prev && prev.from === from ? prev.fromAt : now;
  // Only its start is ever shown, and never a word still being typed: the
  // line grows a whole word at a time.
  const raw = text.slice(from, from + 1000);
  let writing = flatten(raw);
  if (from + raw.length === text.length && !/\s$/.test(text) && writing.includes(' ')) {
    writing = writing.slice(0, writing.lastIndexOf(' '));
  }
  const overdue = !!writing && now - fromAt >= OPEN_AFTER_MS;

  const next = overdue
    ? { line: writing, id: `o${from}`, open: true }
    : last
      ? { line: last.line, id: `c${last.start}`, open: false }
      : { line: '', id: '', open: false };
  const holding = !!prev && !!prev.line && prev.id !== next.id && now - prev.shownAt < HOLD_MS;

  const wakes: number[] = [];
  if (holding) wakes.push(prev.shownAt + HOLD_MS);
  if (writing && !overdue) wakes.push(fromAt + OPEN_AFTER_MS);
  const wake = wakes.length ? Math.min(...wakes) : null;

  if (holding) return { ...prev, from, fromAt, wake };
  const shownAt = prev && prev.id === next.id ? prev.shownAt : now;
  return { ...next, shownAt, from, fromAt, wake };
}

/** "Thought for 7s", "Thought for 1m 12s", or "Thought for a moment" when it
 *  was under a second or nobody timed it. */
export function thoughtFor(seconds: number | null | undefined): string {
  const took = elapsed(seconds);
  return took ? `Thought for ${took}` : 'Thought for a moment';
}

/** "7s", "1m 12s", "2m"; empty under a second, or when nobody timed it. */
export function elapsed(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 1) return '';
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}
